import React from 'react';
import StatusBadge from './StatusBadge';
import { fmt, fmtDateTime, fmtCurrency, fileUrl, fileName } from '../utils/helpers';

function Row({ label, value }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0eeea' }}>
      <span style={{ fontSize: 12, color: '#888', fontWeight: 500, paddingTop: 1 }}>{label}</span>
      <span style={{ fontSize: 14, color: '#1a1a18' }}>{value || '—'}</span>
    </div>
  );
}

function AttachmentRow({ label, path }) {
  if (!path) return null;
  const paths = path.split(',').filter(Boolean);
  if (paths.length === 0) return null;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: 8, padding: '8px 0', borderBottom: '1px solid #f0eeea' }}>
      <span style={{ fontSize: 12, color: '#888', fontWeight: 500, paddingTop: 1 }}>{label}{paths.length > 1 ? ' (' + paths.length + ')' : ''}</span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {paths.map(p => (
          <a key={p} href={fileUrl(p)} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 14, color: '#534AB7', display: 'flex', alignItems: 'center', gap: 6 }}>
            📎 <span style={{ textDecoration: 'underline' }}>{fileName(p)}</span>
            <span style={{ fontSize: 11, color: '#888', marginLeft: 4 }}>↗ open</span>
          </a>
        ))}
      </div>
    </div>
  );
}

export default function RequestDetail({ request }) {
  if (!request) return null;
  const isReimbursement = request.paymentDirect === false;
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <span style={{ fontFamily: 'DM Mono, monospace', fontSize: 13, color: '#534AB7', fontWeight: 500 }}>{request.id}</span>
        <StatusBadge status={request.status} />
      </div>

      <h4 style={{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Payment Details</h4>
      <Row label="Beneficiary (for)" value={request.beneficiaryName} />
      <Row label="Project" value={request.projectName} />
      <Row label="Project Manager" value={request.pmName} />
      <Row label="Payment Type" value={request.paymentType} />
      <Row label="Amount" value={fmtCurrency(request.amount, request.currency)} />
      {(request.jobDateFrom || request.jobDateTo) && <Row label="Job period" value={fmt(request.jobDateFrom) + ' → ' + fmt(request.jobDateTo)} />}
      <Row label="Description" value={request.description} />
      <Row label="Request Date" value={fmt(request.requestDate)} />
      {request.invoiceAvailable !== undefined && request.invoiceAvailable !== null && <Row label="Invoice available?" value={request.invoiceAvailable ? 'Yes' : 'No — no invoice for this payment'} />}

      {isReimbursement && (
        <div style={{ marginTop: 16, background: '#FFF3CD', borderRadius: 10, padding: '12px 16px' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#854F0B', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>💰 Reimbursement — paid to a different recipient</div>
          <div style={{ display: 'grid', gridTemplateColumns: '170px 1fr', gap: '6px 8px', fontSize: 13, color: '#5a3a00' }}>
            <span style={{ fontWeight: 500, color: '#854F0B' }}>Recipient name</span><span>{request.recipientName || '—'}</span>
            <span style={{ fontWeight: 500, color: '#854F0B' }}>Recipient bank</span><span>{request.recipientBank || '—'}</span>
            <span style={{ fontWeight: 500, color: '#854F0B' }}>Recipient account</span><span>{request.recipientAccount || '—'}</span>
            <span style={{ fontWeight: 500, color: '#854F0B' }}>Recipient IBAN</span><span>{request.recipientIban || '—'}</span>
          </div>
        </div>
      )}

      {(request.attachedInvoice || request.attachedDoc) && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 10px' }}>Attachments</h4>
          <AttachmentRow label="Invoices" path={request.attachedInvoice} />
          <AttachmentRow label="Supporting docs" path={request.attachedDoc} />
        </>
      )}

      <h4 style={{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '16px 0 10px' }}>Approval Trail</h4>
      {request.accountantApprovalDate && <Row label="Accountant approved" value={fmt(request.accountantApprovalDate)} />}
      {request.accountantComments && <Row label="Accountant comments" value={request.accountantComments} />}
      {request.mdApprovalDate && <Row label="MD approved" value={fmt(request.mdApprovalDate)} />}
      {request.mdComments && <Row label="MD comments" value={request.mdComments} />}
      {request.batchId && <Row label="Export batch" value={request.batchId} />}
      {request.exportDate && <Row label="Exported on" value={fmt(request.exportDate)} />}

      {request.history && request.history.length > 0 && (
        <>
          <h4 style={{ fontSize: 13, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, margin: '20px 0 10px' }}>History Log</h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {request.history.map((h, i) => (
              <div key={i} style={{ background: '#f9f8f5', borderRadius: 8, padding: '10px 14px', borderLeft: '3px solid #CECBF6' }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 3 }}>{fmtDateTime(h.date)} · {h.user} ({h.role})</div>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#1a1a18' }}>{h.action}</div>
                {h.comments && <div style={{ fontSize: 12, color: '#555', marginTop: 3 }}>{h.comments}</div>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
